import React, { Fragment, useMemo } from 'react'
import {
  AttachmentLink,
  Heading,
  KennoFile,
  Paragraph,
} from '@pandiafi/design-system'
import { Trans, useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'
import { ContextNotification, DetailRow } from '@pandiafi/components'

import {
  Contract,
  ContractTenant,
  ContractTerminationRequest,
} from '../../../../common/validations'
import { useTerminationReasonToLabel } from '../hooks/useTerminationReasonToLabel'
import { useTerminationDestinationToLabel } from '../hooks/useTerminationDestinationToLabel'
import { useShowingSuggestions } from '../hooks/useShowingSuggestions'
import { getTenantName } from '../../../utils/getTenantName'
import { ContractType } from '../../../../common/types'
import { resolveContractTerminationTerminology } from '../../ContractChanges/utils/resolveContractTerminationTerminology'
import { ibanCountryName } from '../utils/bankCountry'

import { Section } from 'components'
import { valueOrEmpty } from 'utils'
import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'
import { CardTitleBand } from 'components/CardTitleBand/CardTitleBand'

interface PreviewData extends Omit<
  ContractTerminationRequest,
  | 'beneficiaries'
  | 'files'
  | 'id'
  | 'movingDestination'
  | 'status'
  | 'terminationReason'
> {
  files: KennoFile[] | string[]
  movingDestination: ContractTerminationRequest['movingDestination'] | ''
  terminationReason: ContractTerminationRequest['terminationReason'] | ''
  beneficiaries: Array<
    Omit<
      ContractTerminationRequest['beneficiaries'][number],
      'returnPercentage' | 'rooReturnPercentage'
    > & {
      existingBeneficiaryId?: number | null
      contractToTenantAssociationId?: number | null
      returnPercentage: number | null
      rooReturnPercentage: number | null
    }
  >
}

interface ContractTerminationRequestPreviewProps {
  contractTerminationRequest: PreviewData
  tenants: ContractTenant[]
  contract: Contract
  confirmButtonLabel: string
  backToEditButtonLabel?: string
}

export const ContractTerminationRequestPreview: React.FC<
  ContractTerminationRequestPreviewProps
> = ({
  contractTerminationRequest,
  tenants,
  contract,
  confirmButtonLabel,
  backToEditButtonLabel,
}) => {
  const { t, i18n } = useTranslation(['common', 'terminateContract'])
  const { terminationReasonToLabel } = useTerminationReasonToLabel()
  const { terminationDestinationToLabel } = useTerminationDestinationToLabel()
  const { showingSuggestions } = useShowingSuggestions()
  const moduleConfig = useModuleConfig()
  const showShowingSection =
    moduleConfig.terminateContract?.showingVisible ?? true

  const {
    movingOutDate,
    endDate,
    displaysSelf,
    presenterTenantUuid,
    additionalDetails,
    givingContactInfoAllowed,
    masterKeyAccessAllowed,
    hasPetsInApartment,
    terminationReason,
    movingDestination,
    newAddress,
    beneficiaries,
    files,
    collateralLender,
    isCollateral,
  } = contractTerminationRequest
  const {
    recipientName,
    streetAddress,
    streetAddressLine2,
    postalCode,
    city,
    countrySubdivision,
    countryName,
    countryCode,
  } = newAddress || {}

  const filesList = useMemo(() => {
    if (!files.length) {
      return valueOrEmpty()
    }

    return files.map((file) => {
      if (typeof file === 'string') {
        return (
          <Paragraph className="mb-1.5 last:mb-0 break-all">{file}</Paragraph>
        )
      }

      return (
        <AttachmentLink
          key={file.fileName}
          href={file._objectUrl}
          text={file.metadata?.originalFileName ?? file.fileName}
          icon={file.fileType === 'image' ? 'image' : 'file'}
          target="_blank"
          className="mb-1.5 last:mb-0 break-all"
        />
      )
    })
  }, [files])

  const isAddressProvided =
    newAddress &&
    Object.values(newAddress).some((value) => value && value.trim() !== '')

  const selectedPresenter = presenterTenantUuid
    ? tenants.find((tenant) => tenant.tenantUuid === presenterTenantUuid)
    : null

  const isCurrentTenantSelectedPresenter =
    selectedPresenter?.id === contract.tenant.id
  const terminology = resolveContractTerminationTerminology(
    contract.contractType
  )

  return (
    <>
      <CardTitleBand>{t(terminology.previewHeading)}</CardTitleBand>
      <Section className="mb-12">
        <ContextNotification type="info" textAlign="center">
          <div className="space-y-2">
            <Paragraph>
              {t('terminateContract:texts.confirmationInstructionReview')}
            </Paragraph>
            <Paragraph>
              <Trans
                t={t}
                i18nKey="terminateContract:texts.confirmationInstructionConfirm"
                values={{
                  confirmButtonLabel,
                }}
                components={{ strong: <strong /> }}
              />
            </Paragraph>
            <Paragraph>{t(terminology.sentAfterConfirmation)}</Paragraph>
            <Paragraph>
              <strong>{t(terminology.returnFromAuthReminder)}</strong>
            </Paragraph>
            {backToEditButtonLabel && (
              <Paragraph>
                <Trans
                  t={t}
                  i18nKey="terminateContract:texts.confirmationInstructionEdit"
                  values={{ backToEditButtonLabel }}
                  components={{ strong: <strong /> }}
                />
              </Paragraph>
            )}
          </div>
        </ContextNotification>
      </Section>
      <Section className="mb-12">
        <Heading size="l" element="h2" className="mb-4">
          {t('terminateContract:headings.basicInfo')}
        </Heading>
        <DetailRow>
          <DetailRow.Item title={t(terminology.submittedLabel)}>
            {tenants.map((tenant) => (
              <span key={tenant.id}>{getTenantName(tenant)}</span>
            ))}
          </DetailRow.Item>
          <DetailRow.Item title={t('terminateContract:labels.movingOutDate')}>
            {movingOutDate ?? t('common:notKnown')}
          </DetailRow.Item>
          <DetailRow.Item title={t('terminateContract:labels.contractEndDate')}>
            {endDate
              ? DateTime.fromISO(endDate).toFormat('dd.MM.yyyy')
              : t('common:notKnown')}
          </DetailRow.Item>
          <DetailRow.Item title={t(terminology.reasonLabel)}>
            {terminationReasonToLabel(terminationReason)}
          </DetailRow.Item>
          <DetailRow.Item
            title={t('terminateContract:labels.movingDestination')}
          >
            {movingDestination
              ? terminationDestinationToLabel(movingDestination)
              : t('common:notKnown')}
          </DetailRow.Item>
          <DetailRow.Item title={t('terminateContract:labels.files')}>
            {filesList}
          </DetailRow.Item>
        </DetailRow>
      </Section>
      <Section className="mb-12">
        <Heading size="l" element="h2" className="mb-8">
          {t('terminateContract:headings.paymentAndContactInfo')}
        </Heading>
        {contract.contractType === ContractType.RIGHT_OF_OCCUPANCY && (
          <DetailRow className="mb-6">
            <DetailRow.Item
              title={t(
                'terminateContract:labels.rightOfOccupancyCollateralLocation'
              )}
            >
              {isCollateral
                ? `${t(
                    'terminateContract:rightOfOccupancyCollateralLocationSuggestions.bank'
                  )}: ${collateralLender || '-'}`
                : t(
                    'terminateContract:rightOfOccupancyCollateralLocationSuggestions.holder'
                  )}
            </DetailRow.Item>
          </DetailRow>
        )}
        {beneficiaries.map(
          (
            {
              beneficiaryName,
              removeExistingBeneficiary,
              iban,
              bic,
              returnPercentage,
              rooReturnPercentage,
              email,
              phoneNumber,
            },
            index
          ) => {
            const bankCountryName = ibanCountryName(iban, i18n.language)

            return (
              !removeExistingBeneficiary && (
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <Fragment key={`${beneficiaryName}-${index}`}>
                  <Heading size="m" element="h3" className="my-4">
                    {beneficiaryName}
                  </Heading>
                  <DetailRow>
                    <DetailRow.Item
                      title={t('terminateContract:labels.beneficiaryName')}
                    >
                      {beneficiaryName ?? '-'}
                    </DetailRow.Item>
                    {contract.contractType ===
                      ContractType.RIGHT_OF_OCCUPANCY && (
                      <DetailRow.Item
                        title={t(
                          'terminateContract:labels.rooReturnPercentage'
                        )}
                      >
                        {rooReturnPercentage === null
                          ? '-'
                          : `${rooReturnPercentage}%`}
                      </DetailRow.Item>
                    )}
                    <DetailRow.Item title={t('terminateContract:labels.iban')}>
                      {iban ?? '-'}
                    </DetailRow.Item>
                    {bankCountryName && (
                      <DetailRow.Item
                        title={t('terminateContract:labels.bankCountry')}
                      >
                        {bankCountryName}
                      </DetailRow.Item>
                    )}
                    {bic && (
                      <DetailRow.Item title={t('terminateContract:labels.bic')}>
                        {bic ?? '-'}
                      </DetailRow.Item>
                    )}
                    <DetailRow.Item
                      title={t('terminateContract:labels.returnPercentage')}
                    >
                      {returnPercentage || '- '}%
                    </DetailRow.Item>
                    <DetailRow.Item title={t('terminateContract:labels.email')}>
                      {valueOrEmpty(email)}
                    </DetailRow.Item>
                    <DetailRow.Item
                      title={t('terminateContract:labels.phoneNumber')}
                    >
                      {valueOrEmpty(phoneNumber)}
                    </DetailRow.Item>
                  </DetailRow>
                </Fragment>
              )
            )
          }
        )}
      </Section>
      {isAddressProvided && (
        <Section className="mb-12">
          <Heading size="l" element="h2" className="mb-4">
            {t('terminateContract:headings.newAddress')}
          </Heading>
          <DetailRow>
            <DetailRow.Item title={t('terminateContract:labels.recipientName')}>
              {recipientName}
            </DetailRow.Item>
            <DetailRow.Item title={t('terminateContract:labels.streetAddress')}>
              {streetAddress}
            </DetailRow.Item>
            {streetAddressLine2 && (
              <DetailRow.Item
                title={t('terminateContract:labels.streetAddressLine2')}
              >
                {streetAddressLine2}
              </DetailRow.Item>
            )}
            <DetailRow.Item title={t('terminateContract:labels.postalCode')}>
              {postalCode}
            </DetailRow.Item>
            <DetailRow.Item title={t('terminateContract:labels.city')}>
              {city}
            </DetailRow.Item>
            {countrySubdivision && (
              <DetailRow.Item
                title={t('terminateContract:labels.countrySubdivision')}
              >
                {countrySubdivision}
              </DetailRow.Item>
            )}
            {countryCode && countryCode !== 'FI' && (
              <DetailRow.Item title={t('terminateContract:labels.countryName')}>
                {countryName}
              </DetailRow.Item>
            )}
          </DetailRow>
        </Section>
      )}

      {showShowingSection && (
        <Section className="mb-12">
          <Heading size="l" element="h2" className="mb-4">
            {t('terminateContract:headings.showing')}
          </Heading>
          {displaysSelf && isCurrentTenantSelectedPresenter && (
            <ContextNotification type="info" className="mb-4">
              {t('terminateContract:texts.selectedAsPresenter')}
            </ContextNotification>
          )}
          <DetailRow>
            <DetailRow.Item title={t('terminateContract:labels.showing')}>
              {displaysSelf
                ? showingSuggestions[0].label
                : showingSuggestions[1].label}
            </DetailRow.Item>
            <DetailRow.Item title={t('terminateContract:labels.presenter')}>
              {selectedPresenter
                ? getTenantName(selectedPresenter)
                : t('common:notKnown')}
            </DetailRow.Item>
            <DetailRow.Item
              title={t('terminateContract:labels.additionalDetails')}
              preLine
            >
              {valueOrEmpty(additionalDetails)}
            </DetailRow.Item>
            <DetailRow.Item
              title={t('terminateContract:labels.givingContactInfoAllowed')}
            >
              {givingContactInfoAllowed ? t('common:yes') : t('common:no')}
            </DetailRow.Item>
            <DetailRow.Item
              title={t('terminateContract:labels.masterKeyAccessAllowed')}
            >
              {masterKeyAccessAllowed ? t('common:yes') : t('common:no')}
            </DetailRow.Item>
            <DetailRow.Item
              title={t('terminateContract:labels.hasPetsInApartment')}
            >
              {hasPetsInApartment ? t('common:yes') : t('common:no')}
            </DetailRow.Item>
          </DetailRow>
        </Section>
      )}

      {!showShowingSection && givingContactInfoAllowed && (
        <Section className="mb-12">
          <Heading size="l" element="h2" className="mb-4">
            {t('terminateContract:headings.contactInformation')}
          </Heading>
          <DetailRow>
            <DetailRow.Item
              title={t('terminateContract:labels.givingContactInfoAllowed')}
            >
              {givingContactInfoAllowed ? t('common:yes') : t('common:no')}
            </DetailRow.Item>
          </DetailRow>
        </Section>
      )}
    </>
  )
}
